import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Tabs, Tab, Card, CardContent, Button, Stack,
  Grid, Avatar, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, Alert, CircularProgress, Divider, Autocomplete,
  InputAdornment
} from '@mui/material';
import {
  Store, Add, Edit, PhotoCamera, Person, Business, Inventory,
  Dashboard, Settings, Visibility, VisibilityOff, Delete,
  ChevronLeft, ChevronRight, CardMembership
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import boutiqueService from '../services/boutiqueService';
import { productService } from '../services/productService';
import userService from '../services/userService';
import categorieService from '../services/categorieService';
import { adminService } from '../services/adminService';
import Notification from '../components/Notification';
import ImageCard from '../components/ImageCard';
import SubscriptionModal from '../components/SubscriptionModal';
import ConfirmationModal from '../components/admin/ConfirmationModal';
import { subscriptionService } from '../services/subscriptionService';

const Vendeur = () => {
  const { user, updateUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: 'success' });

  // Calculer les indices des onglets dynamiquement
  const wantsBoutique = user?.wantsBoutique;
  const hasBoutiqueTab = wantsBoutique === true;

  const TAB_INDICES = {
    DASHBOARD: 0,
    BOUTIQUE: hasBoutiqueTab ? 1 : -1,
    PRODUCTS: hasBoutiqueTab ? 2 : 1,
    PROFILE: hasBoutiqueTab ? 3 : 2
  };

  // Boutique states
  const [boutique, setBoutique] = useState(null);
  const [boutiqueModalOpen, setBoutiqueModalOpen] = useState(false);
  const [boutiqueFormData, setBoutiqueFormData] = useState({
    name: '',
    description: '',
    address: '',
    quartier: '',
    phone: '',
    whatsapp: '',
    email: '',
    openingHours: '',
    specialties: [],
    logo: ''
  });
  const [boutiqueLogoFile, setBoutiqueLogoFile] = useState(null);

  // Product states
  const [products, setProducts] = useState([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productImageIndices, setProductImageIndices] = useState({});
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
    categorieId: null,
    images: [],
    isActive: true,
    isAvailable: true
  });

  // Categories states
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Profile states
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    name: '',
    email: '',
    phone: '',
    profilePhoto: ''
  });
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);

  // Subscription states
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // { type: 'free'|'active'|'expired'|'expiring', daysLeft: number, message: string }
  const [freePlanDays, setFreePlanDays] = useState(30); // Nombre de jours du plan free (par défaut 30)

  // Confirmation modal states
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    title: '',
    message: '',
    onConfirm: null,
    loading: false,
    confirmText: 'Confirmer',
    severity: 'warning'
  });

  useEffect(() => {
    if (user?.role === 'VENDOR' || user?.role === 'ADMIN') {
      loadVendeurData();
      loadCategories();
      loadCurrentSubscription();
      loadFreePlanDays();
      loadSpecialties();
    }
  }, [user]);

  const loadSpecialties = async () => {
    try {
      const response = await adminService.getSystemSetting('boutique_specialties');
      if (response && response.value && Array.isArray(response.value)) {
        setSpecialtyOptions(response.value);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des spécialités:', err);
      // Garder un tableau vide en cas d'erreur
      setSpecialtyOptions([]);
    }
  };

  const loadFreePlanDays = async () => {
    try {
      const response = await adminService.getSystemSetting('free_plan_days');
      if (response && response.value !== null && response.value !== undefined) {
        setFreePlanDays(parseInt(response.value) || 30);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du nombre de jours du plan free:', err);
      // Garder la valeur par défaut de 30
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const response = await subscriptionService.getMySubscription();
      const userCreatedAt = response.userCreatedAt || user?.createdAt;
      const responseFreePlanDays = response.freePlanDays;
      // console.log('userCreatedAt', userCreatedAt);
      // console.log('responseFreePlanDays', responseFreePlanDays);
      // Mettre à jour le nombre de jours du plan free si fourni par la réponse
      if (responseFreePlanDays !== undefined && responseFreePlanDays !== null) {
        setFreePlanDays(parseInt(responseFreePlanDays) || 30);
      }
      
      // Utiliser le nombre de jours du plan free (de la réponse ou de l'état)
      const currentFreePlanDays = responseFreePlanDays !== undefined && responseFreePlanDays !== null 
        ? parseInt(responseFreePlanDays) || 30 
        : freePlanDays;
      
      if (response.success && response.data) {
        setCurrentSubscription(response.data);
        calculateSubscriptionStatus(response.data);
      } else {
        setCurrentSubscription(null);
        // Si pas d'abonnement, vérifier le plan free (nombre de jours configurable depuis la création du compte)
        if (userCreatedAt) {
          const accountCreationDate = new Date(userCreatedAt);
          // console.log('accountCreationDate', accountCreationDate.toLocaleDateString());
          const today = new Date();
          // console.log('today', today.toLocaleDateString());
          const daysSinceCreation = Math.floor((today - accountCreationDate) / (1000 * 60 * 60 * 24));
          // console.log('daysSinceCreation', daysSinceCreation);
          if (daysSinceCreation <= currentFreePlanDays) {
            setSubscriptionStatus({
              type: 'free',
              daysLeft: currentFreePlanDays - daysSinceCreation,
              message: `Vous utilisez le plan free. Il vous reste ${currentFreePlanDays - daysSinceCreation} jour(s).`
            });
          } else {
            setSubscriptionStatus({
              type: 'expired',
              daysLeft: 0,
              message: `Votre période d'essai gratuite de ${currentFreePlanDays} jours est terminée. Vous devez souscrire à une formule d'abonnement pour continuer à utiliser la plateforme.`
            });
          }
        } else {
          // Si pas de date de création, considérer comme expiré
          setSubscriptionStatus({
            type: 'expired',
            daysLeft: 0,
            message: 'Vous devez souscrire à une formule d\'abonnement pour utiliser la plateforme.'
          });
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement de l\'abonnement:', err);
      setCurrentSubscription(null);
    }
  };

  const calculateSubscriptionStatus = (subscription) => {
    if (!subscription) {
      return;
    }

    if (subscription.statut === 'actif' && subscription.dateFin) {
      const endDate = new Date(subscription.dateFin);
      const today = new Date();
      const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

      if (daysLeft < 0) {
        setSubscriptionStatus({
          type: 'expired',
          daysLeft: 0,
          message: 'Votre abonnement a expiré. Veuillez souscrire à une nouvelle formule pour continuer à utiliser la plateforme.'
        });
      } else if (daysLeft <= 3) {
        setSubscriptionStatus({
          type: 'expiring',
          daysLeft: daysLeft,
          message: `Il reste ${daysLeft} jour(s) avant l'expiration de votre abonnement hein .`
        });
      } else if (daysLeft <= 6) {
        setSubscriptionStatus({
          type: 'expiring',
          daysLeft: daysLeft,
          message: `Votre abonnement se termine dans ${daysLeft} jours.`
        });
      } else {
        setSubscriptionStatus({
          type: 'active',
          daysLeft: daysLeft,
          message: `Abonnement actif: ${subscription.formule?.libelle || 'N/A'} - Expire le ${endDate.toLocaleDateString('fr-FR')}`
        });
      }
    } else if (subscription.statut === 'inactif' && !subscription.dateDebut) {
      setSubscriptionStatus({
        type: 'pending',
        daysLeft: null,
        message: 'Votre abonnement est en attente de validation par un administrateur.'
      });
    }
  };

  // Mettre à jour le champ category quand les catégories sont chargées et qu'on est en mode modification
  useEffect(() => {
    if (selectedProduct && categories.length > 0 && productFormData.categorieId) {
      const selectedCategory = categories.find(cat => cat.id === productFormData.categorieId);
      if (selectedCategory) {
        setProductFormData(prev => ({
          ...prev,
          category: selectedCategory.libelle
        }));
      }
    }
  }, [categories, selectedProduct, productFormData.categorieId]);

  const loadVendeurData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger la boutique du vendeur
      const boutiquesData = await boutiqueService.getBoutiques();
      const userBoutique = boutiquesData.boutiques?.find(b => b.vendorId === user.id);

      if (userBoutique) {
        setBoutique(userBoutique);

        // Charger les produits de la boutique (tous les produits, actifs et inactifs)
        try {
          const productsData = await productService.getMyBoutiqueProducts(userBoutique.id);
          setProducts(productsData.products || []);
        } catch (err) {
          console.error('Erreur lors du chargement des produits de la boutique:', err);
          setProducts([]);
        }
      } else {
        // Si pas de boutique, réinitialiser à null
        setBoutique(null);
        
        // Charger les produits sans boutique du vendeur (tous les produits)
        try {
          const response = await productService.getMyProducts();
          setProducts(response.products || []);
        } catch (err) {
          console.error('Erreur lors du chargement des produits sans boutique:', err);
          setProducts([]);
        }
      }

      // Charger les données du profil
      setProfileFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        profilePhoto: user.profilePhoto || ''
      });

    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const categoriesData = await categorieService.getAllCategories();
      setCategories(categoriesData || []);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  // Boutique Management
  const handleBoutiqueEdit = () => {
    if (boutique) {
      setBoutiqueFormData({
        name: boutique.name || '',
        description: boutique.description || '',
        address: boutique.address || '',
        quartier: boutique.quartier || '',
        phone: boutique.phone || '',
        whatsapp: boutique.whatsapp || '',
        email: boutique.email || '',
        openingHours: boutique.openingHours || '',
        specialties: boutique.specialties || [],
        logo: boutique.logo || ''
      });
      setBoutiqueLogoFile(null);
    }
    setBoutiqueModalOpen(true);
  };

  const handleBoutiqueSave = async () => {
    try {
      if (boutique) {
        // Mise à jour de la boutique existante
        const updatedBoutique = await boutiqueService.updateBoutique(boutique.id, boutiqueFormData, boutiqueLogoFile);
        setBoutique(updatedBoutique);
        setBoutiqueLogoFile(null);
        showNotification('Boutique mise à jour avec succès');
      } else {
        // Création d'une nouvelle boutique
        const newBoutique = await boutiqueService.createBoutique({
          ...boutiqueFormData,
          vendorId: user.id
        }, boutiqueLogoFile);
        setBoutique(newBoutique);
        setBoutiqueLogoFile(null);
        showNotification('Boutique créée avec succès');
      }
      setBoutiqueModalOpen(false);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la boutique:', err);
      showNotification('Erreur lors de la sauvegarde', 'error');
    }
  };

  // Product Management
  const handleProductAdd = () => {
    setSelectedProduct(null);
    setProductFormData({
      name: '',
      description: '',
      price: '',
      stock: '',
      category: '',
      categorieId: null,
      images: [],
      isActive: true,
      isAvailable: true
    });
    setProductModalOpen(true);
  };

  // Fonctions pour naviguer entre les images dans les cartes
  const handleProductImageNext = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % totalImages
    }));
  };

  const handleProductImagePrev = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handleProductEdit = (product) => {
    setSelectedProduct(product);
    setProductFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      stock: product.stockQuantity || '',
      category: product.categorie?.libelle || product.category || '',
      categorieId: product.categorieId || product.categorie?.id || null,
      images: product.images || [],
      isActive: product.isActive !== false,
      isAvailable: product.isAvailable !== false
    });
    setProductModalOpen(true);
  };

  const handleProductSave = async () => {
    try {
      const dataToSend = {
        ...productFormData,
        stockQuantity: productFormData.stock,
        categorieId: productFormData.categorieId
      };

      // Si le vendeur a une boutique, l'utiliser, sinon créer sans boutique (formule FREE)
      if (boutique) {
        dataToSend.boutiqueId = boutique.id;
      }
      // Si pas de boutique, ne pas envoyer boutiqueId (le backend créera le produit sans boutique)

      // Supprimer category des données envoyées car on utilise categorieId
      delete dataToSend.category;

      if (selectedProduct) {
        // Mise à jour du produit
        const updatedProduct = await productService.updateProduct(selectedProduct.id, dataToSend);
        setProducts(products.map(p => p.id === selectedProduct.id ? updatedProduct : p));
        showNotification('Produit mis à jour avec succès');
      } else {
        // Création d'un nouveau produit
        const newProduct = await productService.createProduct(dataToSend);
        setProducts([...products, newProduct]);
        showNotification('Produit créé avec succès');
      }
      setProductModalOpen(false);
      // Recharger les données pour mettre à jour la liste
      loadVendeurData();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du produit:', err);
      // Afficher le message d'erreur du backend si disponible
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde';
      showNotification(errorMessage, 'error');
    }
  };

  // Fonction pour ouvrir le modal de confirmation
  const openConfirmationModal = (title, message, onConfirm, confirmText = 'Confirmer', severity = 'warning') => {
    setConfirmationData({
      title,
      message,
      onConfirm,
      loading: false,
      confirmText,
      severity
    });
    setConfirmationModalOpen(true);
  };

  // Fonction pour fermer le modal de confirmation
  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
    setConfirmationData({
      title: '',
      message: '',
      onConfirm: null,
      loading: false,
      confirmText: 'Confirmer',
      severity: 'warning'
    });
  };

  // Fonction pour confirmer l'action
  const handleConfirmAction = async () => {
    if (confirmationData.onConfirm) {
      setConfirmationData(prev => ({ ...prev, loading: true }));
      try {
        await confirmationData.onConfirm();
        closeConfirmationModal();
      } catch (err) {
        console.error('Erreur lors de la confirmation:', err);
        setConfirmationData(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const handleProductDelete = (productId) => {
    const product = products.find(p => p.id === productId);
    const productName = product?.name || 'ce produit';
    
    openConfirmationModal(
      'Supprimer le produit',
      `Êtes-vous sûr de vouloir supprimer le produit "${productName}" ? Cette action est irréversible.`,
      async () => {
        try {
          await productService.deleteProduct(productId);
          setProducts(products.filter(p => p.id !== productId));
          showNotification('Produit supprimé avec succès');
        } catch (err) {
          console.error('Erreur lors de la suppression:', err);
          showNotification('Erreur lors de la suppression', 'error');
          throw err;
        }
      },
      'Supprimer',
      'error'
    );
  };

  // Profile Management
  const handleProfileEdit = () => {
    setProfileModalOpen(true);
  };

  const handleProfileSave = async () => {
    try {
      // Ne pas envoyer email et phone car ils sont désactivés
      const profileDataToSend = {
        name: profileFormData.name,
        profilePhoto: profileFormData.profilePhoto
      };

      // Utiliser le fichier original si une nouvelle photo a été sélectionnée
      // Sinon, profilePhotoFile sera null et seule la mise à jour du nom sera effectuée
      const updatedUser = await userService.updateProfile(profileDataToSend, profilePhotoFile);

      // Mettre à jour le contexte utilisateur
      const updatedUserData = {
        ...user,
        name: updatedUser.name,
        email: updatedUser.email,
        phone: updatedUser.phone,
        profilePhoto: updatedUser.profilePhoto
      };
      updateUser(updatedUserData);

      // Mettre à jour les données locales
      setProfileFormData({
        name: updatedUser.name || '',
        email: updatedUser.email || '',
        phone: updatedUser.phone || '',
        profilePhoto: updatedUser.profilePhoto || ''
      });

      // Réinitialiser le fichier photo après sauvegarde
      setProfilePhotoFile(null);

      showNotification('Profil mis à jour avec succès');
      setProfileModalOpen(false);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      const errorMessage = err.response?.data?.message || 'Erreur lors de la mise à jour';
      showNotification(errorMessage, 'error');
    }
  };

  // Image upload handlers
  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (type === 'boutique') {
      // Pour le logo de boutique, prendre seulement le premier fichier
      const file = files[0];
      if (file.size > 50 * 1024 * 1024) {
        showNotification('L\'image doit faire moins de 50MB', 'error');
        return;
      }
      
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        showNotification('Veuillez sélectionner un fichier image valide', 'error');
        return;
      }
      
      // Stocker le fichier pour l'upload
      setBoutiqueLogoFile(file);
      
      // Créer une preview pour l'affichage
      const reader = new FileReader();
      reader.onload = (event) => {
        setBoutiqueFormData(prev => ({ ...prev, logo: event.target.result }));
      };
      reader.readAsDataURL(file);
    } else if (type === 'product') {
      // Pour les produits, utiliser multer pour uploader les images
      const currentImageCount = productFormData.images ? productFormData.images.length : 0;

      // Vérifier la limite de 5 images maximum
      if (currentImageCount + files.length > 5) {
        showNotification(`Maximum 5 images autorisées. Vous avez ${currentImageCount} image(s) et vous essayez d'ajouter ${files.length}.`, 'error');
        return;
      }

      // Vérifier la taille de chaque fichier (50MB max - sera compressé en WebP)
      const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        showNotification('Les images ne doivent pas dépasser 50MB chacune', 'error');
        return;
      }

      // Vérifier les types de fichiers
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        showNotification('Seules les images (JPEG, JPG, PNG, GIF, WEBP) sont autorisées', 'error');
        return;
      }

      // Uploader les images avec multer
      try {
        const response = await productService.uploadProductImages(files);
        if (response.imageUrls && response.imageUrls.length > 0) {
          setProductFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), ...response.imageUrls]
          }));
          showNotification(`${response.imageUrls.length} image(s) uploadée(s) avec succès`, 'success');
        }
      } catch (error) {
        console.error('Erreur lors de l\'upload des images:', error);
        showNotification(error.response?.data?.message || 'Erreur lors de l\'upload des images', 'error');
      }
    } else if (type === 'profile') {
      // Pour la photo de profil, prendre seulement le premier fichier
      const file = files[0];
      if (file.size > 20 * 1024 * 1024) {
        showNotification('L\'image doit faire moins de 20MB', 'error');
        return;
      }
      // Garder le fichier original pour l'envoi
      setProfilePhotoFile(file);
      // Créer une prévisualisation base64 pour l'affichage
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfileFormData(prev => ({ ...prev, profilePhoto: event.target.result }));
      };
      reader.readAsDataURL(file);
    }

    // Reset the input
    e.target.value = '';
  };

  // Supprimer une image de produit
  const removeProductImage = async (index) => {
    const imageUrl = productFormData.images[index];

    // Si l'image a été uploadée via multer, la supprimer du serveur
    if (imageUrl && imageUrl.includes('/imageProduit/')) {
      try {
        const filename = imageUrl.split('/').pop();
        await productService.deleteProductImage(filename);
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'image:', error);
        // Continuer quand même à supprimer de l'état local
      }
    }

    setProductFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  if (user?.role !== 'VENDOR' && user?.role !== 'ADMIN') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Accès refusé. Cette page est réservée aux vendeurs.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: 'success' })}
      />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Tableau de bord Vendeur
        </Typography>
        {(user?.wantsBoutique === true) && (
          <>
            <Typography variant="body1" color="text.secondary">
              Gérez votre boutique et vos produits
            </Typography>
          </>
        )}
        {(user?.wantsBoutique === false) && (
          <>
            <Typography variant="body1" color="text.secondary">
              Gérez vos produits
            </Typography>
          </>
        )}
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}
            sx={{
              maxWidth: '100vw',
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: 2,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
                fontWeight: 500
              }
            }}
          >
            {/* icon={<Dashboard />}icon={<Store />}icon={<Inventory />}icon={<Person />}  */}
            <Tab label="Tableau de bord" />
            {(user?.wantsBoutique === true) && (
              <Tab label="Ma Boutique" />
            )}

            <Tab label="Mes Produits" />
            <Tab label="Mon Profil" />
          </Tabs>
        </Box>

        <CardContent>
          {/* Tableau de bord */}
          {tabValue === TAB_INDICES.DASHBOARD && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Vue d'ensemble
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CardMembership />}
                  onClick={() => setSubscriptionModalOpen(true)}
                  sx={{ minWidth: 200 }}
                >
                  Souscrire à une formule
                </Button>
              </Box>

              {/* Alerte pour informations boutique incomplètes */}
              {boutique && user?.wantsBoutique === true && (() => {
                const descriptionIncomplete = boutique.description === 'Description de la boutique à compléter' || 
                                             boutique.description?.toLowerCase().includes('description de la boutique à compléter');
                const addressIncomplete = boutique.address === 'adresse à definir' || 
                                         boutique.address === 'adresse à définir' ||
                                         boutique.address?.toLowerCase().includes('adresse à definir') ||
                                         boutique.address?.toLowerCase().includes('adresse à définir');
                const quartierIncomplete = boutique.quartier === 'quartier à définir' || 
                                          boutique.quartier === 'quartier à definir' ||
                                          boutique.quartier?.toLowerCase().includes('quartier à définir') ||
                                          boutique.quartier?.toLowerCase().includes('quartier à definir');
                
                if (descriptionIncomplete || addressIncomplete || quartierIncomplete) {
                  return (
                    <Alert severity="warning" sx={{ mb: 2,md:2 }}>
                      <Typography variant="body2">
                        Veuillez mettre à jour les informations de votre boutique.
                      </Typography>
                    </Alert>
                  );
                }
                return null;
              })()}

              {/* Messages d'abonnement */}
              {subscriptionStatus && (
                <Alert
                  severity={
                    subscriptionStatus.type === 'active' ? 'success' :
                    subscriptionStatus.type === 'free' ? 'info' :
                    subscriptionStatus.type === 'expiring' ? 'warning' :
                    'error'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    {subscriptionStatus.message}
                  </Typography>
                </Alert>
              )}

              {currentSubscription && !subscriptionStatus && (
                <Alert
                  severity={currentSubscription.statut === 'actif' ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Abonnement {currentSubscription.statut === 'actif' ? 'actif' : 'en attente'}:</strong> {currentSubscription.formule?.libelle || 'N/A'}
                    {currentSubscription.statut === 'actif' && currentSubscription.dateFin && (
                      <> - Expire le: {new Date(currentSubscription.dateFin).toLocaleDateString('fr-FR')}</>
                    )}
                    {currentSubscription.statut === 'inactif' && !currentSubscription.dateDebut && (
                      <> - En attente de validation par un administrateur</>
                    )}
                  </Typography>
                </Alert>
              )}

              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mt: 2
              }}>
                {user?.wantsBoutique === true && (
                  <Card sx={{
                    width: 120,
                    height: 120,
                    p: 1.5,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Store sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
                      {boutique ? '1' : '0'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      Boutique(s)
                    </Typography>
                  </Card>
                )}

                <Card sx={{
                  width: 120,
                  height: 120,
                  p: 1.5,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Inventory sx={{ fontSize: 24, color: 'secondary.main', mb: 0.5 }} />
                  <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
                    {products.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Produit(s)
                  </Typography>
                </Card>

                {user?.wantsBoutique === true && (
                  <Card sx={{
                    width: 120,
                    height: 120,
                    p: 1.5,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Visibility sx={{ fontSize: 24, color: 'info.main', mb: 0.5 }} />
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
                      {boutique?.visitCount || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      Visites
                    </Typography>
                  </Card>
                )}

                <Card sx={{
                  width: 120,
                  height: 120,
                  p: 1.5,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Visibility sx={{ fontSize: 24, color: 'success.main', mb: 0.5 }} />
                  <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
                    {products.filter(p => p.isActive).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Actifs
                  </Typography>
                </Card>

                <Card sx={{
                  width: 120,
                  height: 120,
                  p: 1.5,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <VisibilityOff sx={{ fontSize: 24, color: 'warning.main', mb: 0.5 }} />
                  <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
                    {products.filter(p => !p.isActive).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    En attente
                  </Typography>
                </Card>
              </Box>
            </Box>
          )}

          {/* Ma Boutique */}
          {tabValue === TAB_INDICES.BOUTIQUE && hasBoutiqueTab && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Ma Boutique</Typography>

                {(boutique && boutique.isActive === true) && (
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={handleBoutiqueEdit}
                  >
                    {boutique ? 'Modifier' : 'Créer ma boutique'}
                  </Button>
                )}
              </Box>

              {boutique ? (
                <Card sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Avatar
                          src={boutique.logo}
                          sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                        >
                          <Store sx={{ fontSize: 60 }} />
                        </Avatar>
                        <Typography variant="h6" gutterBottom>
                          {boutique.name}
                        </Typography>
                        <Chip
                          label={boutique.isActive ? 'Active' : 'Inactive'}
                          color={boutique.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body1">
                            {boutique.description || 'Aucune description'}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Adresse
                          </Typography>
                          <Typography variant="body1">
                            {boutique.address}, {boutique.quartier}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Contact
                          </Typography>
                          <Typography variant="body1">
                            {boutique.phone && `Tél: ${boutique.phone}`}
                            {boutique.whatsapp && ` | WhatsApp: ${boutique.whatsapp}`}
                            {boutique.email && ` | Email: ${boutique.email}`}
                          </Typography>
                        </Box>

                        {boutique.specialties && boutique.specialties.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Spécialités
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              {boutique.specialties.map((specialty, index) => (
                                <Chip key={index} label={specialty} size="small" />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Card>
              ) : (
                <Card sx={{ p: 4, textAlign: 'center' }}>
                  {/* <Store sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Aucune boutique créée
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Créez votre boutique pour commencer à vendre vos produits
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleBoutiqueEdit}
                  >
                    Créer ma boutique
                  </Button> */}
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Vous boutique n'est pas encore activée ou a été bloquée. Veuillez contacter le support de Tchamba Marketplace.
                  </Alert>
                </Card>
              )}
            </Box>
          )}

          {/* Mes Produits */}
          {tabValue === TAB_INDICES.PRODUCTS && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Mes Produits</Typography>
                {(() => {
                  // Vérifier si la boutique est bloquée ou suspendue
                  const isBoutiqueBlocked = boutique && (boutique.isActive === false || boutique.isSuspended === true);
                  
                  if (isBoutiqueBlocked) {
                    return (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        Votre boutique est bloquée ou en attente de validation. Pour ajouter un produit, veuillez contacter le support.
                      </Alert>
                    );
                  }
                  
                  // Vérifier si l'abonnement est expiré
                  if (subscriptionStatus && subscriptionStatus.type === 'expired') {
                    return (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        Votre abonnement a expiré. Veuillez souscrire à une formule pour ajouter des produits.
                      </Alert>
                    );
                  }
                  
                  return (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleProductAdd}
                    >
                      Ajouter un produit
                    </Button>
                  );
                })()}
              </Box>

              {!boutique && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Vous pouvez créer des produits même sans boutique. Par défaut, vous bénéficiez de la formule FREE.
                  Souscrivez à un abonnement pour augmenter vos limites de produits.
                </Alert>
              )}

              {products.length === 0 ? (
                <Card sx={{ p: 4, textAlign: 'center' }}>
                  <Inventory sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Aucun produit
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Commencez par ajouter vos premiers produits
                  </Typography>
                  {(() => {
                    // Vérifier si la boutique est bloquée ou suspendue
                    const isBoutiqueBlocked = boutique && (boutique.isActive === false || boutique.isSuspended === true);
                    // Vérifier si l'abonnement est expiré
                    const isSubscriptionExpired = subscriptionStatus && subscriptionStatus.type === 'expired';
                    
                    if (!isBoutiqueBlocked && !isSubscriptionExpired) {
                      return (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleProductAdd}
                        >
                          Ajouter un produit
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </Card>
              ) : (
                <Grid
                  container
                  spacing={3}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gridAutoRows: '1fr'
                  }}
                >
                  {products.map((product) => (
                    <Grid   key={product.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            cursor: 'pointer'
                          }}
                        >
                          {(() => {
                            const productImages = Array.isArray(product.images)
                              ? product.images.filter(img => img && typeof img === 'string' && img.trim() !== '')
                              : [];
                            const currentIndex = productImageIndices[product.id] || 0;
                            const hasMultipleImages = productImages.length > 1;
                            const currentImage = productImages.length > 0
                              ? (productImages[currentIndex] || productImages[0])
                              : '/img/no_media.jpg';

                            return (
                              <>
                                <ImageCard
                                  src={currentImage}
                                  alt={product.name}
                                  height={200}
                                />

                                {/* Navigation entre images */}
                                {hasMultipleImages && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleProductImagePrev(e, product.id, productImages.length)}
                                      sx={{
                                        position: 'absolute',
                                        left: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        '&:hover': { bgcolor: 'white' },
                                        zIndex: 2
                                      }}
                                    >
                                      <ChevronLeft fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleProductImageNext(e, product.id, productImages.length)}
                                      sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        '&:hover': { bgcolor: 'white' },
                                        zIndex: 2
                                      }}
                                    >
                                      <ChevronRight fontSize="small" />
                                    </IconButton>
                                    {/* Indicateur du nombre d'images */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        right: 8,
                                        bgcolor: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        zIndex: 2
                                      }}
                                    >
                                      {currentIndex + 1} / {productImages.length}
                                    </Box>
                                  </>
                                )}
                              </>
                            );
                          })()}

                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              display: 'flex',
                              gap: 1,
                              zIndex: 3
                            }}
                          >
                            <Chip
                              label={product.isActive ? 'Activé' : 'En attente'}
                              color={product.isActive ? 'success' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>

                          {(!product.isAvailable || product.stockQuantity === 0) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                bgcolor: 'error.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                zIndex: 3
                              }}
                            >
                              En rupture
                            </Box>
                          )}
                        </Box>

                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            {product.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            paragraph
                            sx={{
                              flexGrow: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {product.description}
                          </Typography>

                          <Box sx={{ mt: 'auto' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                                {parseFloat(product.price || 0).toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Stock: {product.stockQuantity || 0}
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={0.5}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleProductEdit(product)}
                                fullWidth
                                sx={{ textTransform: 'none' }}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={() => handleProductDelete(product.id)}
                                fullWidth
                                sx={{ textTransform: 'none' }}
                              >
                                Supprimer
                              </Button>
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Mon Profil */}
          {tabValue === TAB_INDICES.PROFILE && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Mon Profil</Typography>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={handleProfileEdit}
                >
                  Modifier mon profil
                </Button>
              </Box>

              <Card sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Avatar
                        src={user.profilePhoto}
                        sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                      >
                        <Person sx={{ fontSize: 60 }} />
                      </Avatar>
                      <Typography variant="h6" gutterBottom>
                        {user.name}
                      </Typography>
                      <Chip label={user.role} color="primary" size="small" />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Nom
                        </Typography>
                        <Typography variant="body1">{user.name}</Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">{user.email || 'Non renseigné'}</Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Téléphone
                        </Typography>
                        <Typography variant="body1">{user.phone || 'Non renseigné'}</Typography>
                      </Box>

                      {user?.wantsBoutique === true && boutique && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Boutique
                          </Typography>
                          <Typography variant="body1">{boutique.name || 'Non renseigné'}</Typography>
                        </Box>
                      )}
                      
                      {/* {(!user?.wantsBoutique || !boutique) && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Nom d'entreprise
                          </Typography>
                          <Typography variant="body1">{user.businessName || 'Non renseigné'}</Typography>
                        </Box>
                      )} */}
                    </Stack>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Modal Boutique */}
      <Dialog
        open={boutiqueModalOpen}
        onClose={() => setBoutiqueModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          fontWeight: 600,
          fontSize: '1.5rem'
        }}>
          {boutique ? 'Modifier ma boutique' : 'Créer ma boutique'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Nom de la boutique"
              value={boutiqueFormData.name}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={boutiqueFormData.description}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, description: e.target.value }))}
              variant="outlined"
              placeholder="Décrivez votre boutique..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Adresse"
              value={boutiqueFormData.address}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, address: e.target.value }))}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Quartier"
              value={boutiqueFormData.quartier}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, quartier: e.target.value }))}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Téléphone"
              value={boutiqueFormData.phone}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, phone: e.target.value }))}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="WhatsApp"
              value={boutiqueFormData.whatsapp}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={boutiqueFormData.email}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, email: e.target.value }))}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Horaires d'ouverture"
              value={boutiqueFormData.openingHours}
              onChange={(e) => setBoutiqueFormData(prev => ({ ...prev, openingHours: e.target.value }))}
              placeholder="Ex: Lun-Ven: 9h-18h, Sam: 9h-13h"
              variant="outlined"
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <Autocomplete
              multiple
              freeSolo
              options={specialtyOptions}
              value={boutiqueFormData.specialties || []}
              onChange={(event, newValue) => {
                setBoutiqueFormData(prev => ({ ...prev, specialties: newValue }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Spécialités"
                  placeholder="Sélectionnez ou ajoutez des spécialités"
                  variant="outlined"
                  helperText="Sélectionnez dans la liste ou tapez pour ajouter une nouvelle spécialité"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <Box sx={{
              border: 1,
              borderColor: 'divider',
              p: 2,
              bgcolor: 'grey.50'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Logo de la boutique
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="boutique-logo-upload"
                type="file"
                onChange={(e) => handleImageUpload(e, 'boutique')}
              />
              <label htmlFor="boutique-logo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {boutiqueFormData.logo ? 'Changer le logo' : 'Cliquez pour ajouter un logo'}
                </Button>
              </label>

              {boutiqueFormData.logo && (
                <Box sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{
                    position: 'relative',
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 3
                    }
                  }}>
                    <Avatar
                      src={boutiqueFormData.logo}
                      sx={{
                        width: 120,
                        height: 120
                      }}
                    />
                  </Box>
                  {boutiqueLogoFile && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setBoutiqueLogoFile(null);
                        setBoutiqueFormData(prev => ({ ...prev, logo: boutique?.logo || '' }));
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Retirer
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          gap: 1
        }}>
          <Button
            onClick={() => setBoutiqueModalOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleBoutiqueSave}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            {boutique ? 'Mettre à jour' : 'Créer la boutique'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Produit */}
      <Dialog
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          fontWeight: 600,
          fontSize: '1.5rem'
        }}>
          {selectedProduct ? 'Modifier le produit' : 'Ajouter un produit'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Nom du produit"
              value={productFormData.name}
              onChange={(e) => setProductFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={productFormData.description}
              onChange={(e) => setProductFormData(prev => ({ ...prev, description: e.target.value }))}
              variant="outlined"
              placeholder="Décrivez votre produit..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Prix"
              type="number"
              value={productFormData.price}
              onChange={(e) => setProductFormData(prev => ({ ...prev, price: e.target.value }))}
              required
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      FCFA
                    </Typography>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Quantité en stock"
              type="number"
              value={productFormData.stock}
              onChange={(e) => setProductFormData(prev => ({ ...prev, stock: e.target.value }))}
              required
              variant="outlined"
              inputProps={{ min: 0 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />

            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.libelle || ''}
              value={categories.find(cat => cat.id === productFormData.categorieId) || null}
              onChange={(event, newValue) => {
                setProductFormData(prev => ({
                  ...prev,
                  category: newValue ? newValue.libelle : '',
                  categorieId: newValue ? newValue.id : null
                }));
              }}
              loading={loadingCategories}
              disabled={loadingCategories}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Catégorie"
                  placeholder={loadingCategories ? "Chargement des catégories..." : "Sélectionnez une catégorie"}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.libelle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {option.code}
                  </Typography>
                </Box>
              )}
              noOptionsText={
                loadingCategories
                  ? "Chargement des catégories..."
                  : "Aucune catégorie trouvée"
              }
            />

            <Box sx={{
              border: 1,
              borderColor: 'divider',
              p: 2,
              bgcolor: 'grey.50'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Images du produit
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="product-image-upload"
                type="file"
                multiple
                onChange={(e) => handleImageUpload(e, 'product')}
              />
              <label htmlFor="product-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {productFormData.images.length > 0
                    ? 'Ajouter d\'autres images'
                    : 'Cliquez pour ajouter des images'}
                </Button>
              </label>

              {productFormData.images.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mt: 2
                }}>
                  {productFormData.images.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        border: 1,
                        borderColor: 'divider',
                        boxShadow: 1,
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt={`Image ${index + 1}`}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeProductImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'error.main',
                          color: 'white',
                          width: 28,
                          height: 28,
                          boxShadow: 2,
                          '&:hover': {
                            bgcolor: 'error.dark',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Seul l'admin peut activer/désactiver un produit */}
            {user?.role === 'ADMIN' && (
              <Box sx={{
                border: 1,
                borderColor: 'divider',
                p: 2,
                bgcolor: 'grey.50'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={productFormData.isActive}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Produit actif
                    </Typography>
                  }
                />
              </Box>
            )}

            <Box sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              bgcolor: 'grey.50'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={productFormData.isAvailable}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, isAvailable: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Produit disponible
                  </Typography>
                }
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          gap: 1
        }}>
          <Button
            onClick={() => setProductModalOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleProductSave}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            {selectedProduct ? 'Mettre à jour' : 'Créer le produit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Profil */}
      <Dialog open={profileModalOpen} onClose={() => setProfileModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier mon profil</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar src={profileFormData.profilePhoto} sx={{ width: 80, height: 80 }} />
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-photo-upload"
                    type="file"
                    onChange={(e) => handleImageUpload(e, 'profile')}
                  />
                  <label htmlFor="profile-photo-upload">
                    <Button variant="outlined" component="span" startIcon={<PhotoCamera />}>
                      Changer la photo
                    </Button>
                  </label>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom complet"
                value={profileFormData.name}
                onChange={(e) => setProfileFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={profileFormData.email}
                disabled
                helperText="L'email ne peut pas être modifié"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={profileFormData.phone}
                disabled
                helperText="Le téléphone ne peut pas être modifié"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileModalOpen(false)}>Annuler</Button>
          <Button onClick={handleProfileSave} variant="contained">
            Mettre à jour
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        onSuccess={(message) => {
          setNotification({ message, type: 'success' });
          loadCurrentSubscription();
        }}
        currentSubscription={currentSubscription}
      />

      {/* Modal de confirmation */}
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmAction}
        title={confirmationData.title}
        message={confirmationData.message}
        confirmText={confirmationData.confirmText}
        cancelText="Annuler"
        severity={confirmationData.severity}
        loading={confirmationData.loading}
      />
    </Container>
  );
};

export default Vendeur;
